// components/AddToCalendarButton.js
// PRODUCTION VERSION - Centered dropdown with Google Calendar integration
// FIXED: API URL now uses relative path in production, full URL in development

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// In production, frontend and backend are on same domain, so use relative URL
// In development, frontend is on :3000 and backend is on :3001
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // Production: relative URL (same domain)
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');  // Development: full URL

function AddToCalendarButton({ teeTimeId, playerId, compact = false, addToast }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleAddToGoogleCalendar = async () => {
    console.log('📅 Google Calendar clicked - teeTimeId:', teeTimeId, 'playerId:', playerId);
    setIsLoading(true);
    
    // Build query string with playerId if available
    const queryParams = playerId ? `?playerId=${playerId}` : '';
    
    try {
      // Detect if user is on mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Mobile: Use redirect endpoint to bypass popup blockers
        console.log('📱 Mobile detected - using redirect endpoint');
        const redirectUrl = `${API_URL}/teetimes/${teeTimeId}/calendar/google/redirect${queryParams}`;
        console.log('🔍 Redirecting to:', redirectUrl);
        
        // Direct navigation (no popup blocker)
        window.location.href = redirectUrl;
        
        if (addToast) {
          addToast('Opening Google Calendar...', 'success');
        }
        
        setShowDropdown(false);
      } else {
        // Desktop: Fetch URL and open in new tab (current behavior)
        console.log('💻 Desktop detected - using new tab');
        const url = `${API_URL}/teetimes/${teeTimeId}/calendar/google${queryParams}`;
        console.log('🔍 Fetching from:', url);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ Google Calendar URL received:', data.url);
        
        if (data.url) {
          // Open Google Calendar in new tab
          window.open(data.url, '_blank');
          
          if (addToast) {
            addToast('Opening Google Calendar...', 'success');
          }
        } else {
          throw new Error('No URL received from server');
        }
        
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('❌ Error adding to Google Calendar:', error);
      
      if (addToast) {
        addToast('Failed to add to Google Calendar', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadICS = () => {
    console.log('📅 Download ICS clicked - teeTimeId:', teeTimeId, 'playerId:', playerId);
    
    // Build query string with playerId if available
    const queryParams = playerId ? `?playerId=${playerId}` : '';
    
    try {
      const url = `${API_URL}/teetimes/${teeTimeId}/calendar.ics${queryParams}`;
      console.log('🔍 Downloading from:', url);
      
      // Trigger download
      window.location.href = url;
      
      if (addToast) {
        addToast('Downloading calendar file...', 'success');
      }
      
      setShowDropdown(false);
    } catch (error) {
      console.error('❌ Error downloading ICS:', error);
      
      if (addToast) {
        addToast('Failed to download calendar file', 'error');
      }
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        disabled={isLoading}
        style={{
          minHeight: '40px',
          minWidth: '60px',
          padding: '10px 16px',
          background: 'white',
          border: '1px solid #d1d5db',
          borderRadius: '10px',
          cursor: isLoading ? 'wait' : 'pointer',
          fontSize: '24px',
          opacity: isLoading ? 0.6 : 1,
        }}
        title="Add to Calendar"
      >
        {isLoading ? '⏳' : '📅'}
      </button>

      {showDropdown && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            zIndex: 999999,
            padding: '8px',
            minWidth: '220px',
            maxWidth: '90vw',
          }}
        >
          <button
            onClick={handleAddToGoogleCalendar}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px 16px',
              margin: '4px 0',
              background: 'white',
              color: '#202124',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'wait' : 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.2s',
            }}
            onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#f8f9fa')}
            onMouseOut={(e) => e.currentTarget.style.background = 'white'}
          >
            <span style={{ fontSize: '18px' }}>📅</span>
            Google Calendar
          </button>

          <button
            onClick={handleDownloadICS}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px 16px',
              margin: '4px 0',
              background: 'white',
              color: '#202124',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'wait' : 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.2s',
            }}
            onMouseOver={(e) => !isLoading && (e.currentTarget.style.background = '#f8f9fa')}
            onMouseOut={(e) => e.currentTarget.style.background = 'white'}
          >
            <span style={{ fontSize: '18px' }}>📥</span>
            Download .ics
          </button>

          <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0', padding: '4px 0' }}>
            <button
              onClick={() => setShowDropdown(false)}
              style={{
                width: '100%',
                padding: '8px 16px',
                background: 'white',
                color: '#5f6368',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#f8f9fa'}
              onMouseOut={(e) => e.currentTarget.style.background = 'white'}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default AddToCalendarButton;
